import { HandlerType, StatusType, datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { Loggable } from '@rbi-ctg/frontend';
import LocalStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';

import {
  RBIEnv,
  appVersionCode,
  brand,
  checkRbiEnv,
  country,
  env,
  getConfigValue,
  isLocalDev,
  platform,
} from '../environment';

import { makeBeforeSendCallback } from './make-before-send-callback';

export { StatusType } from '@datadog/browser-logs';

interface Context {
  [x: string]: Loggable | Loggable[];
}

interface DataDogLogger {
  context?: Context;
  message: string | Error;
  status: StatusType;
}

export function init(
  context: Context,
  configuredSampleRate?: number,
  ddSampleRateMparticleEvents?: number
): void {
  let handler: HandlerType = HandlerType.http;

  if (checkRbiEnv(RBIEnv.TEST)) {
    handler = HandlerType.silent;
  }

  const defaultRate = isLocalDev ? 100 : 1;
  const sampleRate = typeof configuredSampleRate === 'number' ? configuredSampleRate : defaultRate;
  const sampleRateMParticle =
    typeof ddSampleRateMparticleEvents === 'number' ? ddSampleRateMparticleEvents : sampleRate;
  const site = getConfigValue('datadog').logs.dataCenter ?? 'datadoghq.com';
  const clientToken = getConfigValue('datadog').logs.clientToken;

  datadogLogs.init({
    clientToken,
    site,
    forwardErrorsToLogs: true,
    env: env(),
    service: `${brand()}_${platform()}`,
    version: appVersionCode(),
    beforeSend: makeBeforeSendCallback(sampleRate, sampleRateMParticle),
  });

  datadogLogs.setLoggerGlobalContext({
    ...datadogLogs.getLoggerGlobalContext(),
    ...context,
  });

  datadogLogs.logger.setHandler(handler);
}

const UNKNOWN_ERROR_MESSAGE: string = 'Unknown application error. Unable to parse error message.';

export function dataDogLogger({ message, context = {}, status }: DataDogLogger): void {
  const ctx = {
    appVersionCode: appVersionCode(),
    brand: brand(),
    country,
    platform: platform(),
    stage: env(),
    userId: LocalStorage?.getItem(StorageKeys.USER)?.cognitoId,
    ...context,
  };

  const strMessage: string =
    typeof message === 'string' ? message : message?.message || UNKNOWN_ERROR_MESSAGE;

  datadogLogs.logger.log(strMessage, ctx, status);
}

export function addContext(key: string, value: string) {
  datadogLogs.logger.addContext(key, value);
}

export function initDatadogRum(context: Context, sampleRate: number, replaySampleRate: number) {
  const FALLBACK_SAMPLING_RATE = isLocalDev ? 100 : 10;
  const awsConfig = getConfigValue('aws');
  const dataDogConfig = getConfigValue('datadog');
  const applicationId = dataDogConfig.rum.applicationId;
  const clientToken = dataDogConfig.rum.clientToken;
  const site = dataDogConfig.rum.dataCenter || 'datadoghq.eu';
  const allowedTracingOrigins = [awsConfig.gqlApiUrl, awsConfig.gqlGatewayApiUrl];

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: `${brand()}_${platform()}`,
    env: env(),
    version: appVersionCode(),
    sampleRate: sampleRate ?? FALLBACK_SAMPLING_RATE,
    sessionReplaySampleRate: replaySampleRate ?? 0,
    trackInteractions: true,
    trackFrustrations: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingOrigins,
    allowedTracingUrls: allowedTracingOrigins,
  });

  datadogRum.setGlobalContext({
    ...datadogRum.getGlobalContext(),
    ...context,
    appVersionCode: appVersionCode(),
    brand: brand(),
    platform: platform(),
    stage: env(),
    country,
  });

  datadogRum.startSessionReplayRecording();
}
